import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Grid,
    Link,
    Typography,
} from '@mui/material';
import './PlagiarismAccordions.css';

const PlagiarismAccordions = ({ results }) => {
    // 🔄 Normalize Originality API vs Old schema
    const normalizeResults = (raw) => {
        if (!raw) return [];

        // New schema: { results: [ { phrase, results: [ ... ] } ] }
        if (Array.isArray(raw.results)) {
            return raw.results.map((item) => ({
                phrase: item.phrase,
                matches:
                    item.results?.map((r) => ({
                        score: (r.scores?.[0]?.score || 0) * 100,
                        website: r.link,
                        title: r.title,
                    })) || [],
            }));
        }

        // Old schema: might already have { phraseResults: [ ... ] }
        if (Array.isArray(raw.phraseResults)) {
            return raw.phraseResults.map((p) => ({
                phrase: p.phrase,
                matches:
                    p.matches?.map((m) => ({
                        score: m.score || 0,
                        website: m.website,
                        title: m.title,
                    })) || [],
            }));
        }

        return [];
    };

    const phraseResults = normalizeResults(results);

    return (
        <div className='PlagPanel__table'>
            <Grid item xs={12}>
                <Grid container paddingLeft={2} paddingRight={5} paddingY={2}>
                    <Grid item xs={12} md={8}>
                        <Typography sx={{ fontWeight: 500 }}>Plagiarized Phrase</Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography sx={{ fontWeight: 500, textAlign: 'center' }}>
                            Matched Websites
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Typography sx={{ fontWeight: 500, textAlign: 'center' }}>
                            Average Score
                        </Typography>
                    </Grid>
                </Grid>

                {phraseResults.map((phraseItem, index) => {
                    const phrase = phraseItem.phrase;
                    const matches = phraseItem.matches;

                    // Average score
                    const totalScore = matches.reduce((acc, curr) => acc + curr.score, 0);
                    const avgScore = matches.length > 0 ? totalScore / matches.length : 0;

                    return (
                        <Accordion key={index} elevation={0}>
                            <AccordionSummary
                                className='PlagPanel__table-row'
                                expandIcon={<ExpandMore />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                            >
                                <Grid container>
                                    <Grid item xs={12} md={8}>
                                        <Typography>"{phrase}"</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                                        <Typography>{matches.length}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={avgScore.toFixed(0) + '%'}
                                            color='error'
                                            variant='filled'
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                {matches.map((match, i) => {
                                    if (i > 9) return null; // limit to 10 results

                                    let displayUrl =
                                        match.website
                                            ?.replace('https://', '')
                                            .replace('http://', '')
                                            .replace('www.', '') || '';

                                    displayUrl =
                                        displayUrl.length > 50
                                            ? displayUrl.slice(0, 50) + '...'
                                            : displayUrl;

                                    return (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                width: '100%',
                                                alignItems: 'center',
                                                gap: '1rem',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <Chip
                                                label={match.score.toFixed(0) + '%'}
                                                color='error'
                                                variant='filled'
                                            />
                                            <Box>
                                                {match.website ? (
                                                    <Link
                                                        href={match.website}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        {match.title || displayUrl}
                                                    </Link>
                                                ) : (
                                                    <Typography>
                                                        {match.title || 'No source link'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                {/* Fallback if no results */}
                {phraseResults.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant='body1' color='textSecondary'>
                            No plagiarism detected.
                        </Typography>
                    </Box>
                )}
            </Grid>
        </div>
    );
};

export default PlagiarismAccordions;
